/**
 * PresentationsConfig - Configuration for PresentationsApp_v2
 * Defines slide-specific node types and presentation settings
 */

import { AppConfig, SupportedClass } from '../../../services/baseApplication/types';

/**
 * Presentations-specific supported classes
 * Uses slide-based containers with spatial positioning within slides
 */
export const PRESENTATIONS_SUPPORTED_CLASSES: Record<string, SupportedClass> = {
  // Slide Container
  'slide': {
    displayName: 'Slide',
    description: 'Presentation slide container',
    category: 'container',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { 
        slideNumber: 1,
        layout: 'blank',
        transition: 'none',
        notes: ''
      }
    }
  },

  // Slide Content (Spatial Elements)
  'slide-title': {
    displayName: 'Slide Title',
    description: 'Main title on slide',
    category: 'text',
    defaultAttrs: {
      position: { type: 'absolute', x: 50, y: 50, width: 900, height: 100 },
      style: { fontSize: 44, fontWeight: 600, textAlign: 'center', color: '#000000' }
    }
  },

  'slide-subtitle': {
    displayName: 'Slide Subtitle',
    description: 'Subtitle text',
    category: 'text',
    defaultAttrs: {
      position: { type: 'absolute', x: 50, y: 160, width: 900, height: 60 },
      style: { fontSize: 28, textAlign: 'center', color: '#000000' }
    }
  },

  'slide-text': {
    displayName: 'Text Box',
    description: 'Text content on slide',
    category: 'text',
    defaultAttrs: {
      position: { type: 'absolute', x: 100, y: 200, width: 800, height: 400 },
      style: { fontSize: 24, lineHeight: 1.5, color: '#000000' }
    }
  },

  'slide-bullet-list': {
    displayName: 'Bullet List',
    description: 'Bulleted list',
    category: 'list',
    defaultAttrs: {
      position: { type: 'absolute', x: 150, y: 200, width: 700, height: 400 },
      style: { fontSize: 24, lineHeight: 1.8, color: '#000000' }
    }
  },

  'slide-numbered-list': {
    displayName: 'Numbered List',
    description: 'Numbered list',
    category: 'list',
    defaultAttrs: {
      position: { type: 'absolute', x: 150, y: 200, width: 700, height: 400 },
      style: { fontSize: 24, lineHeight: 1.8, color: '#000000' }
    }
  },

  // Media
  'slide-image': {
    displayName: 'Image',
    description: 'Image on slide',
    category: 'media',
    defaultAttrs: {
      position: { type: 'absolute', x: 200, y: 200, width: 600, height: 400 },
      semantic: { src: '', alt: '' }
    }
  },

  'slide-video': {
    displayName: 'Video',
    description: 'Video embed',
    category: 'media',
    defaultAttrs: {
      position: { type: 'absolute', x: 200, y: 200, width: 600, height: 400 },
      semantic: { src: '', autoplay: false }
    }
  },

  // Shapes & Visuals
  'slide-shape': {
    displayName: 'Shape',
    description: 'Geometric shape, line, arrow, or custom SVG path',
    category: 'shape',
    defaultAttrs: {
      position: { type: 'absolute', x: 300, y: 300, width: 400, height: 300 },
      semantic: { 
        shapeType: 'rectangle', // 'rectangle' | 'circle' | 'triangle' | 'line' | 'arrow' | 'custom'
        path: '', // SVG path data (required when shapeType='custom')
        // ✅ COORDINATE SYSTEM: Line/arrow endpoints stored as RELATIVE to position.x, position.y
        // AI provides ABSOLUTE coords via start/end format, system converts to relative for storage
        x1: 0,  // Relative to position.x
        y1: 0,  // Relative to position.y
        x2: 0,  // Relative to position.x
        y2: 0   // Relative to position.y
      },
      style: { fill: '#4A90E2' }
    }
  },

  'slide-chart': {
    displayName: 'Chart',
    description: 'Data visualization',
    category: 'chart',
    defaultAttrs: {
      position: { type: 'absolute', x: 150, y: 200, width: 700, height: 400 },
      semantic: { chartType: 'bar', data: [] }
    }
  },

  'slide-table': {
    displayName: 'Table',
    description: 'Data table',
    category: 'table',
    defaultAttrs: {
      position: { type: 'absolute', x: 100, y: 200, width: 800, height: 400 }
    }
  },

  // Interactive
  'slide-link': {
    displayName: 'Link',
    description: 'Hyperlink element',
    category: 'interactive',
    defaultAttrs: {
      position: { type: 'absolute', x: 400, y: 600, width: 200, height: 50 },
      semantic: { url: '', displayText: 'Click here' }
    }
  },

  'slide-button': {
    displayName: 'Button',
    description: 'Interactive button',
    category: 'interactive',
    defaultAttrs: {
      position: { type: 'absolute', x: 400, y: 600, width: 200, height: 60 },
      style: { background: '#4A90E2', color: '#ffffff' }
    }
  },

  // Layout
  'slide-column': {
    displayName: 'Column',
    description: 'Column layout',
    category: 'container',
    defaultAttrs: {
      position: { type: 'absolute', x: 50, y: 150, width: 450, height: 550 }
    }
  },

  'slide-section': {
    displayName: 'Section',
    description: 'Content section',
    category: 'container',
    defaultAttrs: {
      position: { type: 'absolute', x: 50, y: 150, width: 900, height: 550 }
    }
  }
};

/**
 * Slide layouts
 */
export enum SlideLayout {
  BLANK = 'blank',
  TITLE = 'title',
  TITLE_CONTENT = 'title-content',
  TWO_COLUMN = 'two-column',
  COMPARISON = 'comparison',
  TITLE_ONLY = 'title-only',
  SECTION_HEADER = 'section-header',
  CONTENT_CAPTION = 'content-caption',
}

/**
 * Slide transitions
 */
export enum SlideTransition {
  NONE = 'none',
  FADE = 'fade',
  SLIDE = 'slide',
  PUSH = 'push',
  WIPE = 'wipe',
  SPLIT = 'split',
  REVEAL = 'reveal',
  RANDOM = 'random',
}

/**
 * Standard slide dimensions (16:9)
 */
export const SLIDE_DIMENSIONS = {
  width: 1000,
  height: 562.5, // 16:9 aspect ratio
  aspectRatio: 16 / 9
};

/**
 * Presentations Application Configuration
 */
export const PRESENTATIONS_APP_CONFIG: AppConfig = {
  appId: 'presentations_v2',
  appName: 'Presentations v2',
  containerType: 'slide',
  layoutMode: 'slides', // Slides with spatial positioning within each
  supportedClasses: PRESENTATIONS_SUPPORTED_CLASSES,
  supportedPositionTypes: ['flow', 'absolute'], // Slides flow, content is absolute within slides
  supportsDragDrop: true,
  supportsResize: true,
  supportsRotation: true,
  defaultContainer: {
    width: SLIDE_DIMENSIONS.width,
    height: SLIDE_DIMENSIONS.height,
    infinite: false
  },
  
  editorSelector: '.presentations-app-v2' // ✅ Required by AppConfig
};

/**
 * Helper: Get supported className list
 */
export function getSupportedClassNames(): string[] {
  return Object.keys(PRESENTATIONS_SUPPORTED_CLASSES);
}

/**
 * Helper: Check if className is supported
 */
export function isClassNameSupported(className: string): boolean {
  return className in PRESENTATIONS_SUPPORTED_CLASSES;
}

/**
 * Helper: Get class info
 */
export function getClassInfo(className: string): SupportedClass | undefined {
  return PRESENTATIONS_SUPPORTED_CLASSES[className];
}

/**
 * Helper: Check if className is a slide element
 */
export function isSlideClassName(className: string): boolean {
  return className.startsWith('slide-') || className === 'slide';
}

